import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ITenantApiKey } from '@pepperhq/types-sdk';
import React from 'react';
import { styled, Tabs, Tab, Box } from '@mui/material';
import { MainLayout } from 'layouts/MainLayout';
import { ChangeAccessLevelForm, IChangeAccessValues } from 'components/merchant/forms/ChangeAccessLevelForm';
import { IInviteUserFormValues, InviteUserForm } from 'components/merchant/forms/InviteUserForm';
import { Role } from 'components/merchant/models/Role';
import { TenantAccess } from 'components/merchant/models/TenantAccess';
import { tenantAccessApi } from 'components/merchant/tenantAccessApi';
import { useSnackbar } from 'notistack';
import * as routes from 'config/routes';
import {
    addUsersAccess,
    getTenantUsersAccess,
    updateUsersAccess
} from 'store/tenant-access/tenantAccessSlice';
import { Column, Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiTokensPage } from 'components/merchant/api-tokens';
import { useTenantApiKeys } from 'components/merchant/api-keys/useApiKeys';
import { ApiKeyCreatedModal } from 'components/merchant/api-keys/ApiKeyCreatedModal';
import { AddApiKeyForm, IAddApiKeyFormValues } from 'components/merchant/forms/AddApiKeyForm';
import { UserAccessTable } from 'components/merchant/users-access.tsx';
import { useMerchantAppIds } from 'components/merchant/api-tokens/useAppIds';
import { getAllLocations } from 'store/locations/locationsSlice';
import { RolesTab } from 'components/merchant/roles';
import { ApiKeysPage } from 'components/merchant/api-keys';
import { MuiChip } from 'ui/MuiChip';

const PREFIX = 'AccessManagement';

const classes = {
    tabs: `${PREFIX}-tabs`
};

const StyledColumn = styled(Column)(({ theme }) => ({
    [`& .${classes.tabs}`]: {
        zIndex: 1,
        borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        borderBottom: `1px solid ${theme.palette.divider}`
    }
}));

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    minWidth: theme.spacing(60)
}));

const getRoles = async (): Promise<{ roles: Role[]; error?: string }> => {
    try {
        const response = await tenantAccessApi.getRolesList();
        if (response.ok) {
            return { roles: response.body.items };
        }
        throw new Error(response.body.message);
    } catch (error: any) {
        return { error: error.message, roles: [] };
    }
};

const isRolesTabEnabled = import.meta.env.TIER !== 'prod';

const buttons = [
    {
        key: 'users',
        label: 'Users'
    },
    ...(isRolesTabEnabled
        ? [
              {
                  key: 'roles',
                  label: 'Roles',
                  isNew: true
              }
          ]
        : []),
    {
        key: 'api',
        label: 'API Tokens'
    },
    {
        key: 'api-keys',
        label: 'API Keys'
    }
];

export const AccessManagementPage = (): React.ReactElement => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { tenantUsersAccess } = useAppSelector(state => state.tenantAccess);
    const { locations: currentLocations } = useAppSelector(state => state.locations);

    const navigate = useNavigate();
    const { tabId } = useParams<{ tabId?: string }>();

    const { appIdDetails, handleAddAppId } = useMerchantAppIds();
    const { apiKeyDetails, handleAddApiKey, handleToggleApiKey, handleDeleteApiKey } = useTenantApiKeys();

    const [isLoading, setLoading] = React.useState(true);
    const [addApiKeyOpen, setAddApiKeyOpen] = React.useState(false);
    const [createdApiKey, setCreatedApiKey] = React.useState<ITenantApiKey<string> | null>(null);
    const [inviteUserOpen, setInviteUserOpen] = React.useState(false);
    const [isResend, setIsResend] = React.useState(false);
    const [isModalLoading, setModalLoading] = React.useState(false);
    const [currentTenantAccessId, setCurrentTenantAccessId] = React.useState<string | null>(null);
    const [currentRoles, setRoles] = React.useState<Role[] | null>(null);

    const handleChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: string) => {
            navigate(routes.MERCHANT_CONFIG.replace(':tabId', newValue));
        },
        [navigate]
    );

    React.useEffect(() => {
        (async function () {
            await dispatch(getTenantUsersAccess());
            const { roles, error: rolesError } = await getRoles();
            if (!rolesError) {
                setRoles(roles);
            }
            await dispatch(getAllLocations());
            setLoading(false);
        })();
    }, [dispatch]);

    const handleToggleAddApiKeyOpen = React.useCallback(() => {
        setAddApiKeyOpen(prev => !prev);
    }, []);

    const handleAddApiKeySubmit = React.useCallback(
        async (values: IAddApiKeyFormValues) => {
            const newKey = await handleAddApiKey(values.description, values.applicationIdPrefix);
            if (newKey) {
                setAddApiKeyOpen(false);
                setCreatedApiKey(newKey);
            }
        },
        [handleAddApiKey]
    );

    const handleCreatedApiKeyClose = React.useCallback(() => {
        setCreatedApiKey(null);
    }, []);

    const handleToggleInviteOpen = React.useCallback(() => {
        setInviteUserOpen(!inviteUserOpen);
        if (!!inviteUserOpen) {
            setIsResend(false);
            setCurrentTenantAccessId(null);
        }
    }, [inviteUserOpen]);
    const handleTenantAccessEditClose = React.useCallback(() => {
        setCurrentTenantAccessId(null);
    }, []);
    const handleToggleResendInvite = React.useCallback(
        (tenantAccess: TenantAccess) => {
            handleToggleInviteOpen();
            setCurrentTenantAccessId(tenantAccess.id);
            setIsResend(true);
            setModalLoading(false);
        },
        [handleToggleInviteOpen, setIsResend, setModalLoading]
    );
    const handleChangeAccessLevelClick = React.useCallback(
        (tenantAccessId: string) => () => {
            setCurrentTenantAccessId(tenantAccessId);
        },
        [setCurrentTenantAccessId]
    );

    const handleInviteSubmit = React.useCallback(
        async (values: IInviteUserFormValues) => {
            const result = await tenantAccessApi.invite({
                roleId: values.roleId,
                email: values.email,
                locationIds: values.locationIds.map(({ value }) => value)
            });
            if (result.ok) {
                const updatedUserIndex = tenantUsersAccess.findIndex(
                    item => item.email === result.body.email
                );
                if (updatedUserIndex >= 0) {
                    dispatch(updateUsersAccess(updatedUserIndex, result.body));
                } else {
                    dispatch(addUsersAccess(result.body));
                }

                setInviteUserOpen(false);
                enqueueSnackbar('Your invite has been successfully sent', { variant: 'success' });
            } else {
                enqueueSnackbar(result.body.message, { variant: 'error' });
            }
            setModalLoading(false);
        },
        [tenantUsersAccess, enqueueSnackbar, dispatch]
    );

    const handleChangeAccessSubmit = React.useCallback(
        async (values: IChangeAccessValues) => {
            setModalLoading(true);
            const result = await tenantAccessApi.updateUserAccess(currentTenantAccessId, {
                roleId: values.roleId,
                locationIds: values.locationIds ? values.locationIds.map(({ value }) => value) : []
            });
            if (!result.ok) {
                setModalLoading(false);
                enqueueSnackbar(`Error updating user access level: ${result.body.message}`, {
                    variant: 'error'
                });
                return;
            }
            const index = tenantUsersAccess.findIndex(
                tenantAccess => tenantAccess.id === currentTenantAccessId
            );
            dispatch(updateUsersAccess(index, result.body));
            setModalLoading(false);
            setCurrentTenantAccessId(null);
            enqueueSnackbar(`Access level was successfully updated for ${result.body.email}`, {
                variant: 'success'
            });
        },
        [currentTenantAccessId, dispatch, enqueueSnackbar, tenantUsersAccess]
    );

    const currentTenantAccess = React.useMemo(
        () =>
            tenantUsersAccess &&
            tenantUsersAccess.find(tenantAccess => tenantAccess.id === currentTenantAccessId),
        [tenantUsersAccess, currentTenantAccessId]
    );

    const changeAccessInitialValues = React.useMemo(() => {
        if (!currentTenantAccess) {
            return undefined;
        }
        return {
            roleId: currentTenantAccess.roleId,
            locationIds: currentTenantAccess.locationIds
                ? currentTenantAccess.locationIds.map((locationId: string) => ({
                      value: locationId,
                      label: currentLocations.find(location => location._id === locationId)?.title
                  }))
                : []
        };
    }, [currentTenantAccess, currentLocations]);
    const resendInitialValues = React.useMemo(() => {
        if (!isResend || !currentTenantAccess) {
            return undefined;
        }
        return {
            email: currentTenantAccess.email,
            ...changeAccessInitialValues
        };
    }, [isResend, currentTenantAccess, changeAccessInitialValues]);

    return (
        <React.Fragment>
            <MainLayout
                pageName="Access management"
                pageDescription="Merchant users and API credentials for interaction with Pepper"
                noScroll
            >
                <StyledColumn flex={1}>
                    <Tabs
                        className={classes.tabs}
                        value={tabId}
                        onChange={handleChange}
                        indicatorColor="primary"
                        variant="scrollable"
                    >
                        {buttons.map(({ label, key, isNew }, index) => (
                            <Tab
                                key={`label-${index}`}
                                value={key}
                                label={
                                    isNew ? (
                                        <Row valign="center">
                                            {label}
                                            <MuiChip
                                                label="new"
                                                color="purple"
                                                sx={{
                                                    ml: 1,
                                                    fontSize: '0.8rem',
                                                    textTransform: 'none',
                                                    height: '20px'
                                                }}
                                            />
                                        </Row>
                                    ) : (
                                        <span>{label}</span>
                                    )
                                }
                            />
                        ))}
                    </Tabs>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0
                        }}
                    >
                        {tabId === 'users' && (
                            <UserAccessTable
                                isLoading={isLoading}
                                currentRoles={currentRoles}
                                currentLocations={currentLocations}
                                onChangeAccessLevelClick={handleChangeAccessLevelClick}
                                onToggleInviteOpen={handleToggleInviteOpen}
                                onToggleResendInvite={handleToggleResendInvite}
                            />
                        )}
                        {tabId === 'api' && (
                            <ApiTokensPage appIdDetails={appIdDetails} onAppIdAdd={handleAddAppId} />
                        )}
                        {tabId === 'api-keys' && (
                            <ApiKeysPage
                                apiKeyDetails={apiKeyDetails}
                                onToggleAddOpen={handleToggleAddApiKeyOpen}
                                onToggleApiKey={handleToggleApiKey}
                                onDeleteApiKey={handleDeleteApiKey}
                            />
                        )}
                        {tabId === 'roles' && isRolesTabEnabled && <RolesTab currentRoles={currentRoles} />}
                    </Box>
                </StyledColumn>
            </MainLayout>
            <StyledMuiModal
                open={inviteUserOpen}
                onClose={handleToggleInviteOpen}
                title={isResend ? 'Resend User Invite' : 'Invite User'}
                overflowVisible
            >
                <InviteUserForm
                    onClose={handleToggleInviteOpen}
                    isLoading={isModalLoading}
                    onSubmit={handleInviteSubmit}
                    roles={currentRoles}
                    locations={currentLocations}
                    isResend={isResend}
                    initialValues={resendInitialValues}
                />
            </StyledMuiModal>
            <StyledMuiModal
                open={!!currentTenantAccessId && !isResend}
                onClose={handleTenantAccessEditClose}
                title="Change Access Level"
                overflowVisible
            >
                <ChangeAccessLevelForm
                    onClose={handleTenantAccessEditClose}
                    isLoading={isModalLoading}
                    onSubmit={handleChangeAccessSubmit}
                    roles={currentRoles}
                    locations={currentLocations}
                    initialValues={changeAccessInitialValues}
                />
            </StyledMuiModal>
            <StyledMuiModal open={addApiKeyOpen} onClose={handleToggleAddApiKeyOpen} title="Add API Key">
                <AddApiKeyForm
                    onClose={handleToggleAddApiKeyOpen}
                    isLoading={!!apiKeyDetails.loading}
                    onSubmit={handleAddApiKeySubmit}
                />
            </StyledMuiModal>
            <ApiKeyCreatedModal apiKey={createdApiKey} onClose={handleCreatedApiKeyClose} />
        </React.Fragment>
    );
};
